'use client';
import React, { useEffect, useRef } from 'react';

interface WaveBackgroundProps {
  className?: string;
  opacity?: number;
}

export default function WaveBackground({ className = '', opacity = 0.6 }: WaveBackgroundProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let animationId: number;
    let time = 0;

    const resize = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };

    resize();
    window.addEventListener('resize', resize);

    const drawWave = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      time += 0.008;

      const waves = [
        { amplitude: 60, frequency: 0.008, speed: 1, yOffset: 0.55, color: 'rgba(72, 180, 248, 0.35)', lineWidth: 1.5 },
        { amplitude: 45, frequency: 0.010, speed: 1.3, yOffset: 0.60, color: 'rgba(72, 180, 248, 0.25)', lineWidth: 1 },
        { amplitude: 70, frequency: 0.006, speed: 0.8, yOffset: 0.50, color: 'rgba(139, 201, 240, 0.20)', lineWidth: 1 },
        { amplitude: 35, frequency: 0.012, speed: 1.6, yOffset: 0.65, color: 'rgba(72, 180, 248, 0.15)', lineWidth: 0.8 },
        { amplitude: 55, frequency: 0.007, speed: 1.1, yOffset: 0.58, color: 'rgba(0, 188, 125, 0.10)', lineWidth: 0.8 },
      ];

      waves.forEach((wave) => {
        ctx.beginPath();
        ctx.lineWidth = wave.lineWidth;
        ctx.strokeStyle = wave.color;

        for (let x = 0; x <= canvas.width; x += 2) {
          const y =
            canvas.height * wave.yOffset +
            Math.sin(x * wave.frequency + time * wave.speed) * wave.amplitude +
            Math.sin(x * wave.frequency * 0.5 + time * wave.speed * 0.7) * (wave.amplitude * 0.4);

          if (x === 0) {
            ctx.moveTo(x, y);
          } else {
            ctx.lineTo(x, y);
          }
        }
        ctx.stroke();
      });

      // Draw dots on waves
      const dotWave = waves[0];
      for (let x = 0; x <= canvas.width; x += 30) {
        const y =
          canvas.height * dotWave.yOffset +
          Math.sin(x * dotWave.frequency + time * dotWave.speed) * dotWave.amplitude +
          Math.sin(x * dotWave.frequency * 0.5 + time * dotWave.speed * 0.7) * (dotWave.amplitude * 0.4);
        
        const alpha = 0.3 + Math.sin(x * 0.05 + time) * 0.2;
        ctx.beginPath();
        ctx.arc(x, y, 1.5, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(72, 180, 248, ${alpha})`;
        ctx.fill();
      }

      animationId = requestAnimationFrame(drawWave);
    };

    drawWave();

    return () => {
      cancelAnimationFrame(animationId);
      window.removeEventListener('resize', resize);
    };
  }, []);

  return (
    <canvas data-component-id="src/components/ui/WaveBackground.tsx:88:4" data-component-path="src/components/ui/WaveBackground.tsx" data-component-line="88" data-component-end-line="92" data-component-file="WaveBackground.tsx" data-component-name="canvas" data-component-content="%7B%22elementName%22%3A%22canvas%22%7D"
      ref={canvasRef}
      className={`absolute inset-0 w-full h-full pointer-events-none ${className}`}
      style={{ opacity }}
    />
  );
}
